import type {
  Identity,
  Invite,
  InviteResponseResult,
  InviteResult,
  Me,
  OrganizationUserPayload,
  Role,
} from '@ankor-io/common/types/account'
import type { UserWithConnectedAccounts } from '@ankor-io/common/types/awx'
import type { ListVesselUsersPayload, VesselUserPayload } from '@ankor-io/common/types/pay'

import { type HttpConfig, http } from '@/api/client/http'
import {
  createIamOrganizationPaths,
  createOrganizationPaths,
  createRootPaths,
  createVesselConnectedAccountPaths,
  createVesselPaths,
} from '@/api/paths'

type ListUsersParams = {
  take?: number
  skip?: number
}

type ListUsersWithMinRoleParams = {
  take?: number
  skip?: number
  minRole?: Role
}

type UpdateUserRolesBody = {
  roles: Role[]
}

type CreateInviteBody = {
  email: string
  roles: Role[]
  awxConnectedAccountId?: string
}

type CreateVesselInviteBody = {
  email: string
  roles: Role[]
  awxConnectedAccountIds: string[]
  connectionId?: string
}

type AddUserToConnectedAccountsBody = {
  userId: string
  connectedAccountIds: string[]
}

type RemoveUserFromConnectedAccountsBody = {
  userId: string
  connectedAccountIds: string[]
}

type AddUsersToConnectedAccountBody = {
  userIds: string[]
}

type RemoveUsersFromConnectedAccountBody = {
  userIds: string[]
}

export const userService = {
  // ========== Root-level Methods ==========

  /**
   * Get current user profile with organizations and preferences.
   */
  getMe(config?: HttpConfig): Promise<Me> {
    const paths = createRootPaths()
    return http.get<Me>(paths.me(), config)
  },

  /**
   * Get invite by ID (no authentication required).
   */
  getInvite(inviteId: string, config?: HttpConfig): Promise<InviteResponseResult> {
    const paths = createRootPaths()
    const url = paths.getInvite(inviteId)
    return http.get<{ result: InviteResponseResult }>(url, config).then((res) => res.result)
  },

  // ========== Organization-level Methods (IAM) ==========

  /**
   * List users in an organization.
   */
  listOrganizationUsers(
    orgId: string,
    params?: ListUsersParams,
    config?: HttpConfig,
  ): Promise<{ identity: Identity; roles: Role[] }[]> {
    const paths = createIamOrganizationPaths(orgId)
    const usersPath = paths.organizationUsers()

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${usersPath}?${queryString}` : usersPath

    return http.get<{ result: { identity: Identity; roles: Role[] }[] }>(url, config).then((res) => res.result)
  },

  /**
   * List organization users with vessel access (identity, roles, vessels with connected accounts).
   */
  listOrganizationUsersWithAccess(
    orgId: string,
    params?: ListUsersParams,
    config?: HttpConfig,
  ): Promise<OrganizationUserPayload[]> {
    const paths = createIamOrganizationPaths(orgId)
    const accessPath = paths.organizationUsersWithAccess()

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }
    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${accessPath}?${queryString}` : accessPath

    return http.get<{ result: OrganizationUserPayload[] }>(url, config).then((res) => res.result)
  },

  /**
   * Update user roles in an organization.
   */
  updateOrganizationUserRoles(
    orgId: string,
    userId: string,
    payload: UpdateUserRolesBody,
    config?: HttpConfig,
  ): Promise<{ success: boolean }> {
    const paths = createIamOrganizationPaths(orgId)
    const url = paths.organizationUserRoles(userId)

    return http.put<{ success: boolean }, UpdateUserRolesBody>(url, payload, config)
  },

  /**
   * Remove user from an organization.
   */
  removeOrganizationUser(orgId: string, userId: string, config?: HttpConfig): Promise<{ success: boolean }> {
    const paths = createIamOrganizationPaths(orgId)
    const url = paths.organizationUser(userId)

    return http.delete<{ success: boolean }>(url, config)
  },

  /**
   * Leave an organization (self removal).
   */
  leaveOrganization(orgId: string, config?: HttpConfig): Promise<{ success: boolean; nextOrganizationId: string }> {
    const paths = createIamOrganizationPaths(orgId)
    const url = paths.leaveOrganization()

    return http.delete<{ success: boolean; nextOrganizationId: string }>(url, config)
  },

  // ========== Organization-level Methods (Pay) ==========

  /**
   * List organization invites.
   */
  listOrganizationInvites(orgId: string, params?: ListUsersParams, config?: HttpConfig): Promise<Invite[]> {
    const paths = createOrganizationPaths(orgId)
    const invitesPath = paths.organizationInvites()

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${invitesPath}?${queryString}` : invitesPath

    return http.get<{ invites: Invite[] }>(url, config).then((res) => res.invites)
  },

  /**
   * Create an organization invite.
   */
  createOrganizationInvite(orgId: string, payload: CreateInviteBody, config?: HttpConfig): Promise<Invite> {
    const paths = createOrganizationPaths(orgId)
    const url = paths.createOrganizationInvite()

    return http.post<{ invite: Invite }, CreateInviteBody>(url, payload, config).then((res) => res.invite)
  },

  /**
   * Create a vessel invite with module (connected account) access.
   */
  createVesselInvite(
    orgId: string,
    vesselId: string,
    payload: CreateVesselInviteBody,
    config?: HttpConfig,
  ): Promise<Invite> {
    const paths = createVesselPaths(orgId, vesselId)
    const url = paths.invite()
    return http.post<{ invite: Invite }, CreateVesselInviteBody>(url, payload, config).then((res) => res.invite)
  },

  /**
   * Get and accept an organization invite.
   */
  getOrganizationInvite(orgId: string, inviteId: string, config?: HttpConfig): Promise<InviteResult> {
    const paths = createOrganizationPaths(orgId)
    const url = paths.getOrganizationInvite(inviteId)

    return http.get<{ result: InviteResult }>(url, config).then((res) => res.result)
  },

  /**
   * Delete an organization invite.
   */
  deleteOrganizationInvite(orgId: string, inviteId: string, config?: HttpConfig): Promise<{ success: boolean }> {
    const paths = createOrganizationPaths(orgId)
    const url = paths.deleteOrganizationInvite(inviteId)

    return http.delete<{ success: boolean }>(url, config)
  },

  /**
   * Get user access to connected accounts.
   */
  getUserAccessToConnectedAccounts(
    orgId: string,
    params?: ListUsersParams,
    config?: HttpConfig,
  ): Promise<UserWithConnectedAccounts[]> {
    const paths = createOrganizationPaths(orgId)
    const accessPath = paths.userAccessToConnectedAccounts()

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${accessPath}?${queryString}` : accessPath

    return http
      .get<{ usersWithConnectedAccounts: UserWithConnectedAccounts[] }>(url, config)
      .then((res) => res.usersWithConnectedAccounts)
  },

  /**
   * Add user to multiple connected accounts.
   */
  addUserToConnectedAccounts(
    orgId: string,
    payload: AddUserToConnectedAccountsBody,
    config?: HttpConfig,
  ): Promise<{ message: string; userId: string; connectedAccountIds: string[] }> {
    const paths = createOrganizationPaths(orgId)
    const url = paths.addUserToConnectedAccounts()

    return http.post<
      { message: string; userId: string; connectedAccountIds: string[] },
      AddUserToConnectedAccountsBody
    >(url, payload, config)
  },

  /**
   * Remove user from multiple connected accounts.
   */
  removeUserFromConnectedAccounts(
    orgId: string,
    payload: RemoveUserFromConnectedAccountsBody,
    config?: HttpConfig,
  ): Promise<{ message: string }> {
    const paths = createOrganizationPaths(orgId)
    const url = paths.removeUserFromConnectedAccounts()

    // DELETE with body: use data in config
    return http.delete<{ message: string }>(url, { ...config, data: payload })
  },

  /**
   * List users in a vessel.
   * Returns empty array if the API omits the users field.
   */
  listVesselUsers(
    orgId: string,
    vesselId: string,
    params?: ListUsersParams,
    config?: HttpConfig,
  ): Promise<VesselUserPayload[]> {
    const paths = createIamOrganizationPaths(orgId)
    const usersPath = paths.vesselUsers(vesselId)

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${usersPath}?${queryString}` : usersPath

    return http.get<ListVesselUsersPayload>(url, config).then((res) => res.users ?? [])
  },

  // ========== Vessel-level Methods (Pay) ==========

  /**
   * List in-progress invites for a vessel (org + vessel scoped, no connected account).
   */
  listVesselInProgressInvites(
    orgId: string,
    vesselId: string,
    params?: ListUsersParams,
    config?: HttpConfig,
  ): Promise<Invite[]> {
    const paths = createVesselPaths(orgId, vesselId)
    const invitesPath = paths.vesselUsersInvites()

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${invitesPath}?${queryString}` : invitesPath

    return http.get<{ invites: Invite[] }>(url, config).then((res) => res.invites)
  },

  // ========== Connected Account-level Methods (Pay) ==========

  /**
   * List invites for a connected account.
   */
  listConnectedAccountInvites(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    params?: ListUsersParams,
    config?: HttpConfig,
  ): Promise<Invite[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const invitesPath = paths.connectedAccountUsersInvites()

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${invitesPath}?${queryString}` : invitesPath

    return http.get<{ invites: Invite[] }>(url, config).then((res) => res.invites)
  },

  /**
   * List users for a connected account with an option minimum role.
   */
  listConnectedAccountUsersWithMinRole(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    params?: ListUsersWithMinRoleParams,
    config?: HttpConfig,
  ): Promise<{ identity: Identity; roles: Role[] }[]> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const usersPath = paths.connectedAccountAllUsers()

    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }

    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }

    if (params?.minRole !== undefined) {
      searchParams.set('minRole', String(params.minRole))
    }

    const queryString = searchParams.toString()
    const url = queryString ? `${usersPath}?${queryString}` : usersPath

    return http.get<{ result: { identity: Identity; roles: Role[] }[] }>(url, config).then((res) => res.result)
  },

  /**
   * Add users to a connected account.
   */
  addUsersToConnectedAccount(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    payload: AddUsersToConnectedAccountBody,
    config?: HttpConfig,
  ): Promise<{ message: string; userIds: string[] }> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.addUsersToConnectedAccount()

    return http.post<{ message: string; userIds: string[] }, AddUsersToConnectedAccountBody>(url, payload, config)
  },

  /**
   * Remove users from a connected account.
   */
  removeUsersFromConnectedAccount(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    payload: RemoveUsersFromConnectedAccountBody,
    config?: HttpConfig,
  ): Promise<{ message: string; userIds: string[] }> {
    const paths = createVesselConnectedAccountPaths(orgId, vesselId, connectedAccountId)
    const url = paths.removeUsersFromConnectedAccount()

    // DELETE with body: use data in config
    return http.delete<{ message: string; userIds: string[] }>(url, { ...config, data: payload })
  },
}
